/**
 * Run Session Provider
 * Handles global persistence and recovery of run sessions
 * Independent of page navigation
 */

import { useEffect, useRef } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useRunSession } from '@/store/run-session';
import { trackingService } from '@/services/tracking-service';
import { logger } from '@/lib/logger';
import { useToast } from '@/hooks/use-toast';

export function RunSessionProvider({ children }: { children: React.ReactNode }) {
  const { status, sessionId, resumeRun, setPartialState } = useRunSession();
  const { toast } = useToast();
  const isNative = Capacitor.isNativePlatform();
  const hasRestoredRef = useRef(false);

  // Validate persisted state values
  const validateRunState = (state: any) => {
    const now = Date.now();
    const oneYearAgo = now - (365 * 24 * 60 * 60 * 1000);
    
    return {
      ...state,
      duration: (state.duration > 0 && state.duration < 86400) ? state.duration : 0,
      distance: (state.distance >= 0 && state.distance < 500000) ? state.distance : 0,
      startTime: (state.startTime > oneYearAgo && state.startTime <= now) ? state.startTime : now,
      pausedDuration: (state.pausedDuration >= 0 && state.pausedDuration < 86400000) ? state.pausedDuration : 0,
    };
  };

  // Restore session on mount
  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    const restoreSession = async () => {
      const runFinished = sessionStorage.getItem('run_finished') === 'true';
      
      if (runFinished) {
        logger.info('Run already finished, not restoring');
        return;
      }

      const activeRunState = sessionStorage.getItem('active_run_state');
      
      if (activeRunState) {
        try {
          const parsedState = JSON.parse(activeRunState);
          const validatedState = validateRunState(parsedState);
          
          // Check if validation changed values (data corruption detected)
          const wasCorrupted = JSON.stringify(parsedState) !== JSON.stringify(validatedState);
          
          if (wasCorrupted) {
            logger.warn('Corrupted run state detected and corrected', { 
              original: parsedState, 
              validated: validatedState 
            });
            sessionStorage.removeItem('active_run_state');
            return;
          }
          
          if (status !== 'idle') {
            const isServiceActive = trackingService.isActive();
            
            if (isServiceActive && status === 'running') {
              logger.info('Restoring active run session', { 
                sessionId,
                status 
              });
              
              // Resume tracking service
              await trackingService.resumeTracking();
              
              toast({
                title: 'Corrida recuperada',
                description: 'Sua corrida continua ativa.',
                duration: 3000,
              });
            } else if (status === 'paused') {
              logger.info('Run was paused, waiting for user action');
            }
          }
        } catch (error) {
          logger.error('Failed to parse run state, clearing', error);
          sessionStorage.removeItem('active_run_state');
        }
      }
    };

    restoreSession();
  }, []);

  // Native app lifecycle listeners
  useEffect(() => {
    if (!isNative) return;

    let pauseListener: any;
    let resumeListener: any;

    const setupListeners = async () => {
      // Save state when app goes to background
      pauseListener = await CapacitorApp.addListener('pause', () => {
        if (status === 'running' || status === 'paused') {
          logger.info('App paused, persisting run state', { status, sessionId });
          sessionStorage.setItem('active_run_state', JSON.stringify({
            status,
            sessionId,
            timestamp: Date.now(),
          }));
        }
      });

      // Restore state when app comes to foreground
      resumeListener = await CapacitorApp.addListener('resume', async () => {
        logger.info('App resumed, checking run state');
        
        // Check if run is finished before restoring
        if (sessionStorage.getItem('run_finished') === 'true') {
          logger.info('Run marked as finished, not restoring from app resume');
          return;
        }

        if (status === 'running') {
          const isServiceActive = trackingService.isActive();
          
          if (isServiceActive) {
            logger.info('Resuming tracking after app resume');
            await trackingService.resumeTracking();
          } else {
            logger.warn('Service not active after resume, restarting (preserving points)');
            // Service stopped, need to restart WITHOUT clearing points
            if (sessionId) {
              await trackingService.restartTracking();
            }
          }
        }
      });
    };

    setupListeners();

    return () => {
      if (pauseListener) {
        pauseListener.remove();
      }
      if (resumeListener) {
        resumeListener.remove();
      }
    };
  }, [isNative, status, sessionId]);

  // Web visibility change listener (fallback)
  useEffect(() => {
    if (isNative) return;

    const handleVisibilityChange = async () => {
      const runSession = useRunSession.getState();
      
      // Detect and reset corrupted state
      if (runSession.duration > 86400 || runSession.duration < 0) {
        logger.error('Corrupted duration detected, resetting run session', { 
          duration: runSession.duration 
        });
        runSession.resetRun();
        return;
      }
      
      if (document.hidden) {
        // Page hidden
        if (status === 'running' || status === 'paused') {
          logger.info('Page hidden, persisting run state', { status });
          sessionStorage.setItem('active_run_state', JSON.stringify({
            status,
            sessionId,
            timestamp: Date.now(),
          }));
        }
      } else {
        // Page visible
        logger.info('Page visible, checking run state');
        
        // Check if run is finished before restoring
        if (sessionStorage.getItem('run_finished') === 'true') {
          logger.info('Run marked as finished, not restoring from visibility change');
          return;
        }

        if (status === 'running') {
          const isServiceActive = trackingService.isActive();
          
          if (!isServiceActive && sessionId) {
            logger.warn('Service not active after visibility change, restarting (preserving points)');
            await trackingService.restartTracking();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isNative, status, sessionId]);

  return <>{children}</>;
}
